/**
 * SalesReportPage — 매출 리포트 (차트 3종 + 상세 테이블).
 *
 * 【역할】
 * - 기간별 매출 추이 시각화 + 상세 리스트 열람
 * - 뷰포트 내 1화면 fit (body scroll 없음) — 차트 고정 높이 + 테이블이 잔여 높이 흡수
 *
 * 【주요 기능】
 * - 검색 (기간 + 고객명 검색)
 * - 차트 3종 단일 행 (일별 / 고객별 / 상품별)
 * - 하단 DataTable 은 남은 높이 채움
 *
 * 【사용 훅】
 * - useFramework / useDataSet
 * - useLabel
 *
 * 【Backend RPC serviceName】
 * - order/searchList  (action=search) — 주문 목록을 원천 데이터로 사용, 클라이언트에서 집계
 *
 * 【DataSet 구조】
 * - ds_list: 매출 원천 row (orderDate / customerName / productName / totalAmount)
 *
 * 【사용처】 router/index.tsx — `{ path: 'sales-report' }`
 * 【포팅 원본】 vue-spring-fw/frontend/src/pages/PageSalesReport.vue
 */
import { useEffect, useMemo } from 'react'
import { Form, Card, Row, Col } from 'antd'
import ReactECharts from 'echarts-for-react'
import dayjs, { Dayjs } from 'dayjs'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormInput from '@/components/common/FormInput'
import { FormDateRangePicker } from '@/components/common/FormDatePicker'
import { useFramework, useDataSet } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { fmtDate } from '@/utils/format'

interface SalesRow {
  orderDate: string
  orderNo?: string
  customerName: string
  status?: string
  productName?: string
  totalAmount: number
}

// 차트 영역 높이: 그리드와 시각적 밸런스(≈ 차트 40% / 그리드 60%) 유지
const CHART_HEIGHT = 280
const CHART_ROW_HEIGHT = CHART_HEIGHT + 24 // card body padding(8*2) + 여백

/**
 * SalesReportPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function SalesReportPage() {
  const [form] = Form.useForm()
  const { $transaction, loading } = useFramework()
  const { t } = useLabel()
  const listDs = useDataSet<SalesRow>([])

  const rows = useMemo<SalesRow[]>(
    () => listDs.rows.filter((r) => r._state !== 'D').map((r) => r.data),
    [listDs.rows]
  )

  const fn_search = async () => {
    const v = form.getFieldsValue()
    const [from, to] = (v.dateRange ?? []) as [Dayjs, Dayjs]
    await $transaction({
      id: 'select',
      service: 'order/searchList',
      action: 'search',
      inds: {
        ds_search: {
          dateFrom: from?.format('YYYY-MM-DD'),
          dateTo: to?.format('YYYY-MM-DD'),
          customerName: v.customerName
        }
      },
      outds: { ds_order: listDs }
    })
  }

  useEffect(() => {
    // 초기 기간: 최근 12개월 — V050 seed 데이터 전범위 커버
    form.setFieldsValue({
      dateRange: [dayjs().subtract(12, 'month').startOf('month'), dayjs()],
      customerName: ''
    })
    void fn_search()
  }, [])

  // 백엔드 totalAmount 는 문자열로 내려올 수 있음 — 반드시 Number 로 변환
  const toNum = (v: unknown): number => (v == null ? 0 : Number(v) || 0)

  /**
   * orderDate 는 서버에서 ISO timestamp(`YYYY-MM-DDTHH:mm:ss`) 로 내려오므로
   * 날짜 집계 키는 앞 10자만 추출해 `YYYY-MM-DD` 로 정규화한다.
   */
  const toDateKey = (v: string | undefined): string => (v ? String(v).slice(0, 10) : '-')

  /** 일별 매출 추이 — 정규화된 날짜를 category X축으로, 비어있는 날짜는 0 으로 보간 */
  const trendOption = useMemo(() => {
    const byDate: Record<string, number> = {}
    rows.forEach((r) => {
      const k = toDateKey(r.orderDate)
      byDate[k] = (byDate[k] ?? 0) + toNum(r.totalAmount)
    })
    const dates = Object.keys(byDate).sort()
    return {
      title: { text: '일별 매출 추이', left: 'center', textStyle: { fontSize: 13 } },
      grid: { left: 56, right: 16, top: 36, bottom: 40 },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { fontSize: 10, rotate: dates.length > 10 ? 45 : 0 }
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => (v >= 1e6 ? `${Math.round(v / 1e4)}만` : String(v)) }
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number | undefined) => (v ?? 0).toLocaleString() + '원'
      },
      series: [{ type: 'line', data: dates.map((d) => byDate[d]), smooth: true, areaStyle: {} }]
    }
  }, [rows])

  /** 고객별 매출 비중 — Top 8 + 기타 통합 (파이가 너무 많이 쪼개지지 않도록) */
  const customerOption = useMemo(() => {
    const byCust: Record<string, number> = {}
    rows.forEach((r) => {
      const name = r.customerName || '(미상)'
      byCust[name] = (byCust[name] ?? 0) + toNum(r.totalAmount)
    })
    const sorted = Object.entries(byCust).sort((a, b) => b[1] - a[1])
    const top = sorted.slice(0, 8)
    const etc = sorted.slice(8).reduce((s, [, v]) => s + v, 0)
    const data = [
      ...top.map(([name, value]) => ({ name, value })),
      ...(etc > 0 ? [{ name: '기타', value: etc }] : [])
    ]
    return {
      title: { text: '고객별 매출 비중 (Top 8)', left: 'center', textStyle: { fontSize: 13 } },
      tooltip: {
        trigger: 'item',
        valueFormatter: (v: number | undefined) => (v ?? 0).toLocaleString() + '원'
      },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['35%', '65%'],
          center: ['50%', '55%'],
          data,
          label: { fontSize: 10 }
        }
      ]
    }
  }, [rows])

  /**
   * 월별 매출 추이 — productName 필드가 백엔드 응답에 없기 때문에
   * "상품별 Top10" 대신 월별 매출 집계로 대체 (의미 있는 시각화).
   */
  const monthlyOption = useMemo(() => {
    const byMonth: Record<string, number> = {}
    rows.forEach((r) => {
      const mk = String(r.orderDate ?? '').slice(0, 7) // YYYY-MM
      if (!mk) return
      byMonth[mk] = (byMonth[mk] ?? 0) + toNum(r.totalAmount)
    })
    const months = Object.keys(byMonth).sort()
    return {
      title: { text: '월별 매출 추이', left: 'center', textStyle: { fontSize: 13 } },
      grid: { left: 56, right: 16, top: 36, bottom: 28 },
      xAxis: { type: 'category', data: months, axisLabel: { fontSize: 10 } },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => (v >= 1e6 ? `${Math.round(v / 1e4)}만` : String(v)) }
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number | undefined) => (v ?? 0).toLocaleString() + '원'
      },
      series: [
        {
          type: 'bar',
          data: months.map((m) => byMonth[m]),
          itemStyle: { borderRadius: [4, 4, 0, 0] }
        }
      ]
    }
  }, [rows])

  return (
    <AppPage
      search={{
        form,
        onSearch: fn_search,
        loading,
        content: (
          <>
            <Form.Item name="dateRange" label={t('LBL_PERIOD', '기간')}>
              <FormDateRangePicker />
            </Form.Item>
            <Form.Item name="customerName" label={t('COL_CUSTOMER_NAME', '고객명')}>
              <FormInput style={{ width: 160 }} />
            </Form.Item>
          </>
        )
      }}
      toolbar={{
        onSearch: fn_search,
        onExport: () => {
          /* TODO */
        },
        permissions: { R: true, E: true }
      }}
    >
      <Row gutter={12} style={{ flex: `0 0 ${CHART_ROW_HEIGHT}px`, marginBottom: 12 }}>
        <Col span={8}>
          <Card size="small" styles={{ body: { padding: 8 } }}>
            <ReactECharts option={trendOption} style={{ height: CHART_HEIGHT }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" styles={{ body: { padding: 8 } }}>
            <ReactECharts option={customerOption} style={{ height: CHART_HEIGHT }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small" styles={{ body: { padding: 8 } }}>
            <ReactECharts option={monthlyOption} style={{ height: CHART_HEIGHT }} />
          </Card>
        </Col>
      </Row>

      <DataTable<SalesRow>
        rowKey={(r, idx) => r.orderNo ?? `${r.orderDate}-${r.customerName}-${idx ?? ''}`}
        dataSource={rows}
        columns={[
          {
            title: t('LBL_DATE', '일자'),
            dataIndex: 'orderDate',
            width: 120,
            render: (v) => fmtDate(v)
          },
          { title: t('LBL_ORDER_NO', '주문번호'), dataIndex: 'orderNo', width: 180 },
          { title: t('COL_CUSTOMER_NAME', '고객명'), dataIndex: 'customerName' },
          { title: t('COL_STATUS', '상태'), dataIndex: 'status', width: 120 },
          {
            title: t('COL_AMOUNT', '금액'),
            dataIndex: 'totalAmount',
            width: 160,
            align: 'right',
            render: (v) => toNum(v).toLocaleString()
          }
        ]}
      />
    </AppPage>
  )
}
